import { action, observable, computed } from 'mobx';
import { IDropdownOption, IApiSelectSource } from '~domain/entities/Config';
import { RampPgApiSdk } from '~api/RampPgApiSdk';
import { handlePlaceholderValues } from '~utils/FormatText';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';

export interface IApiDropDownData {
    [key: string]: string;
}

// For single suggestion data item
interface IApiDropDownSuggestionItem {
    [key: string]: IApiDropDownData;
}

// For all suggestion data items
interface IApiDropDownSuggestionItems {
    [key: string]: IApiDropDownSuggestionItem;
}

// For ApiDropDown's option
export interface IApiDropDownSuggestions {
    [key: string]: IDropdownOption[];
}

export interface IApiConfig extends Omit<IApiSelectSource, 'triggerLength' | 'type'> {
    fieldName: string;
    fieldValue: string;
}

export class ApiDropDownRepository {
    private api: RampPgApiSdk;
    @observable private _suggestions: IApiDropDownSuggestions = {};
    @observable private suggestionItems: IApiDropDownSuggestionItems = {};

    constructor() {
        this.api = diContainer.get(serviceNames.rampPgApiSdk);
    }

    public loadSuggestionData = async (apiConfig: IApiConfig) => {
        const { fieldName, fieldValue, value: endpoint, brandRegionQuery } = apiConfig;
        const path = handlePlaceholderValues(endpoint, {
            [fieldName]: fieldValue
        });
        try {
            const inputData = await this.api.loadSuggestionApiJsonContent(path, brandRegionQuery);

            this.setSuggestion(apiConfig, inputData);
        } catch (e) {
            console.error(e);
        }
    };

    @action
    private setSuggestion = (apiConfig: IApiConfig, json: IApiDropDownData[]) => {
        const { fieldName, formatLabel } = apiConfig;

        this._suggestions[fieldName] = json.map((node) => {
            const label = handlePlaceholderValues(formatLabel, node);

            this.suggestionItems[fieldName] = {
                [label]: node
            };

            return {
                label,
                value: label
            };
        });
    };

    @computed
    public get suggestions() {
        return this._suggestions;
    }

    @action
    public resetSuggestion = (fieldName: string) => {
        delete this._suggestions[fieldName];
    };

    @action
    public resetSuggestionItems = (fieldName: string) => {
        delete this.suggestionItems[fieldName];
    };

    public getSuggestionItem = (fieldName: string, fieldValue: string) =>
        this.suggestionItems[fieldName][fieldValue];
}
